import React, { useState, useEffect } from 'react';
import css from './SectionBuy.module.css';
import {
  findLargestImageUrl,
  getDetailsList,
  getListingImages,
  normalizeTags,
} from './utils/utils';
import { getDownloadUrlFromFirebase, downloadFileFromFirebase } from '../../../../util/firebase';
import EmblaCarousel from './utils/EmblaCarousel';
import { LISTING_STATE_CLOSED } from '../../../../util/types';

const SectionBuy = ({
  listing,
  listingConfig,
  formattedPrice,
  onSubmit,
  isOwnListing,
  processType,
}) => {
  const [isOpen, setOpen] = useState(false);
  const [downloadLoading, setDownloadLoading] = useState(false);
  const firebaseVideo = listing?.attributes?.publicData?.firebaseVideo;
  const [firebaseVideoUrl, setFirebaseVideoUrl] = useState(null);
  useEffect(() => {
    if (!firebaseVideo) return;
    getDownloadUrlFromFirebase({ fileName: firebaseVideo, folderName: 'listings-videos' })
      .then((resp) => {
        if (resp?.url) {
          setFirebaseVideoUrl(resp.url);
        }
      })
      .catch((error) => {
        console.error('Error getting download URL:', error);
      });
  }, []);

  if (!listing?.images?.length || !listing?.attributes?.publicData?.categoryLevel1) return <></>;
  const imageUrl = findLargestImageUrl(listing.images);
  const details = getDetailsList({ listing, listingConfig });
  const allImages = getListingImages(listing);
  const youtubeUrl = listing?.attributes?.publicData?.youtubeVideoUrl;
  const embedYoutubeUrl = youtubeUrl && youtubeUrl.replace('watch?v=', 'embed/');
  const tagsNormalizedString = normalizeTags(listing);
  const avatarImage = listing?.author?.profileImage?.attributes?.variants?.['square-small2x']?.url;
  const authorHandle = listing?.author?.attributes?.profile?.displayName;
  const isCurrentlyClosed = listing?.attributes?.state === LISTING_STATE_CLOSED;
  const submitDisabled = isCurrentlyClosed || isOwnListing;

  const handleBuyNow = () => {
    if (isOwnListing || isCurrentlyClosed) return null;
    const values = {
      quantity: '1',
      deliveryMethod: 'none',
    };
    onSubmit(values);
  };

  const handleDownload = async () => {
    if (isOwnListing || isCurrentlyClosed) return null;
    const pointCloudFile = listing?.attributes?.publicData?.pointCloudFile;
    if (!pointCloudFile) return;

    try {
      setDownloadLoading(true);
      await downloadFileFromFirebase({
        fileName: pointCloudFile,
        folderName: 'point-clouds',
      });
      setDownloadLoading(false);
    } catch (e) {
      console.error('Error downloading file:', e);
      setDownloadLoading(false);
    }
  };

  return (
    <div className={css.wrapper}>
      <div className={css.contentWrapper}>
        <div className={css.left}>
          <div className={css.imageWrapper} onClick={() => setOpen(true)}>
            <img src={imageUrl} alt={listing.title} className={css.image} />
          </div>
          <div className={css.detailsWrapper}>
            {details.map((i) => (
              <div className={css.detailsItem}>
                <p className={css.detailsLabel}>{i.label}</p>
                <p className={css.detailsValue}>{i.value}</p>
              </div>
            ))}
          </div>
        </div>
        <div className={css.right}>
          <div className={css.detailsItem}>
            <p className={css.detailsLabel}>Title:</p>
            <p className={css.detailsTitleValue}>{listing.attributes.title}</p>
          </div>
          {formattedPrice && (
            <div className={css.detailsItem}>
              <p className={css.detailsLabel}>Price:</p>
              <p className={css.detailsPriceValue}>{formattedPrice}</p>
            </div>
          )}
          <div className={css.detailsItem}>
            <p className={css.detailsLabel}>Overview</p>
            <p className={`${css.detailsValue} ${css.textPaddingRight}`}>
              {listing.attributes.description}
            </p>
          </div>

          {tagsNormalizedString && (
            <div className={css.detailsItem}>
              <p className={css.detailsLabel}>Tags:</p>
              <p className={css.detailsValue}>{tagsNormalizedString}</p>
            </div>
          )}

          <div className={css.detailsItem}>
            <p className={css.detailsLabel}>Creator (Verified):</p>
            <div className={css.detailsCreatorValue}>
              {avatarImage && (
                <img src={avatarImage} alt={'profile image'} className={css.avatarImage} />
              )}
              <p className={css.authorHandle}>@{authorHandle}</p>
            </div>

            {processType === 'inquiry' ? (
              <button
                type="button"
                className={submitDisabled ? css.submitDisabled : css.submitButton}
                onClick={handleDownload}
                disabled={submitDisabled || downloadLoading}
              >
                <span>{downloadLoading ? 'Downloading...' : 'Download now'}</span>
              </button>
            ) : (
              <button
                type="button"
                className={submitDisabled ? css.submitDisabled : css.submitButton}
                onClick={handleBuyNow}
              >
                <span>Buy Now</span>
              </button>
            )}
          </div>
        </div>
      </div>

      {isOpen && (
        <div className={css.modalShown} onClick={() => setOpen(false)}>
          <div className={css.modalClose} onClick={() => setOpen(false)}>
            X
          </div>
          <div className={css.modalContent} onClick={(e) => e.stopPropagation()}>
            <EmblaCarousel
              slides={[
                ...allImages,
                { type: 'youtube', value: embedYoutubeUrl },
                { type: 'video', value: firebaseVideoUrl },
              ]
                .filter((i) => i.value)
                .map((i) => {
                  if (i.type === 'youtube') {
                    return (
                      <iframe
                        className={css.youtubeIframe}
                        src={embedYoutubeUrl}
                        title="YouTube video player"
                        frameborder="0"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                        referrerpolicy="strict-origin-when-cross-origin"
                        allowfullscreen
                      ></iframe>
                    );
                  }
                  if (i.type === 'video')
                    return (
                      <video className={css.modelVideo} controls>
                        <source src={i.value} type="video/mp4" />
                        Your browser does not support the video tag.
                      </video>
                    );
                  return <img className={css.sliderImage} src={i.value} />;
                })}
              options={{ loop: true }}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default SectionBuy;
